import { Patient, Condition, Coding } from '@medplum/fhirtypes'
import { createReference } from '@medplum/core'
import { useMedplum } from '@medplum/react'
import { ChartItem } from './PatientChart.utils.tsx'
import { Select, Tabs, TextInput } from '@mantine/core'
import { DateInput } from '@/components/ui/DateInput'
import { parseDateInputValue } from '@/utils/dateInput.ts'
import { formatDateTime } from '@/utils.ts'
import { ResourceTable } from './ResourceTable.tsx'
import { useState } from 'react'
import { DxCodingSelect } from '@/pages/Call/components/DxCodingSelect'
import { AddResourceSection } from './AddResourceSection.tsx'

type Props = {
  patient: Patient
  item: ChartItem
}

const availFields = [
  { code: 'description', title: 'Description' },
  { code: 'type', title: 'Type' },
  { code: 'icd10', title: 'ICD-10-CM' },
  { code: 'snomed', title: 'SNOMED CT®' },
  { code: 'clinical-status', title: 'Status' },
  { code: 'onset-date', title: 'Started' },
  { code: 'ended', title: 'Ended' },
  { code: 'doctor', title: 'Practitioner' },
]

const getCode = (condition: Condition, system: string) => {
  return (
    condition.code?.coding?.find((c) => c.system?.includes(system))?.code ||
    condition.code?.coding?.find((c) => c.system?.toLowerCase().includes(system))?.code ||
    '-'
  )
}

const renderColumn = (condition: Condition, itemCode: string) => {
  switch (itemCode) {
    case 'description':
      return condition.code?.text || condition.code?.coding?.[0]?.display || '-'
    case 'type':
      return condition.category?.[0]?.coding?.[0]?.display || 'Diagnosis'
    case 'icd10':
      return getCode(condition, 'icd-10')
    case 'snomed':
      return getCode(condition, 'snomed')
    case 'clinical-status':
      return condition.clinicalStatus?.coding?.[0]?.code || '-'
    case 'onset-date':
      return condition.onsetDateTime || condition.onsetPeriod?.start
        ? formatDateTime(condition.onsetDateTime || condition.onsetPeriod?.start || '')
        : '-'
    case 'ended':
      return condition.abatementDateTime || condition.onsetPeriod?.end
        ? formatDateTime(condition.abatementDateTime || condition.onsetPeriod?.end || '')
        : '-'
    case 'doctor':
      return condition.asserter?.display || '-'
    default:
      return ''
  }
}

const setQuery = (itemSource: ChartItem, query: string): ChartItem => {
  const item = { ...itemSource }
  let operator = '&'
  if (!item.extraQuery) {
    operator = ''
    item.extraQuery = ''
  }

  item.extraQuery += operator + query
  return item
}

const preprocessList = (list: Condition[]): Condition[] => {
  const resourceList: Condition[] = []

  for (const item of list) {
    if (resourceList.some((i) => !!i.recordedDate && i.recordedDate === item.recordedDate)) {
      continue
    }
    resourceList.push(item)
  }

  return resourceList
}

const CLINICAL_STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'recurrence', label: 'Recurrence' },
  { value: 'relapse', label: 'Relapse' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'remission', label: 'Remission' },
  { value: 'resolved', label: 'Resolved' },
]

const VERIFICATION_STATUS_OPTIONS = [
  { value: 'unconfirmed', label: 'Unconfirmed' },
  { value: 'provisional', label: 'Provisional' },
  { value: 'differential', label: 'Differential' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'refuted', label: 'Refuted' },
  { value: 'entered-in-error', label: 'Entered in error' },
]

export const Diagnoses = ({ patient, item }: Props) => {
  const medplum = useMedplum()
  const [currentTab, setCurrentTab] = useState('active')
  const headerCellClassName = 'text-[16px] whitespace-nowrap'

  const [coding, setCoding] = useState<Coding | undefined>(undefined)
  const [clinicalStatus, setClinicalStatus] = useState<string | null>('active')
  const [verificationStatus, setVerificationStatus] = useState<string | null>('confirmed')
  const [onsetDate, setOnsetDate] = useState<Date | null>(null)
  const [note, setNote] = useState('')

  const resetForm = () => {
    setCoding(undefined)
    setClinicalStatus('active')
    setVerificationStatus('confirmed')
    setOnsetDate(null)
    setNote('')
  }

  const buildResource = (): Condition | { error: string } => {
    if (!coding?.code) return { error: 'Please select a diagnosis code.' }
    const profile = medplum.getProfile()
    return {
      resourceType: 'Condition',
      subject: { reference: `Patient/${patient.id}` },
      ...(profile?.resourceType === 'Practitioner' && { asserter: createReference(profile) }),
      code: {
        coding: [coding],
        text: coding.display || coding.code,
      },
      clinicalStatus: clinicalStatus
        ? {
            coding: [
              {
                system: 'http://terminology.hl7.org/CodeSystem/condition-clinical',
                code: clinicalStatus,
                display: clinicalStatus,
              },
            ],
          }
        : undefined,
      verificationStatus: verificationStatus
        ? {
            coding: [
              {
                system: 'http://terminology.hl7.org/CodeSystem/condition-ver-status',
                code: verificationStatus,
                display: verificationStatus,
              },
            ],
          }
        : undefined,
      onsetDateTime: onsetDate ? onsetDate.toISOString() : undefined,
      recordedDate: new Date().toISOString(),
      note: note.trim() ? [{ text: note.trim() }] : undefined,
    }
  }

  return (
    <AddResourceSection<Condition>
      patient={patient}
      title="Diagnoses"
      modalTitle="Add Diagnosis"
      resourceType="Condition"
      featureContext="Condition"
      noun="diagnosis"
      isSubmitDisabled={!coding?.code}
      resetForm={resetForm}
      buildResource={buildResource}
      renderForm={() => (
        <>
          <div>
            <p className="mb-1 text-sm font-medium">Diagnosis (ICD-10) *</p>
            <DxCodingSelect
              value={coding}
              patientId={patient.id}
              placeholder="Search diagnosis"
              onChange={setCoding}
            />
          </div>
          <Select
            label="Clinical Status"
            data={CLINICAL_STATUS_OPTIONS}
            value={clinicalStatus}
            onChange={setClinicalStatus}
          />
          <Select
            label="Verification Status"
            data={VERIFICATION_STATUS_OPTIONS}
            value={verificationStatus}
            onChange={setVerificationStatus}
          />
          <DateInput
            label="Onset Date"
            value={onsetDate}
            onChange={(value) => setOnsetDate(parseDateInputValue(value))}
            clearable
            placeholder="Pick a date"
          />
          <TextInput
            label="Notes"
            value={note}
            onChange={(e) => setNote(e.currentTarget.value)}
            placeholder="Optional notes"
          />
        </>
      )}
    >
      {(refreshKey) => (
        <>
          <Tabs
            defaultValue="active"
            className="mb-4"
            onChange={(value) => setCurrentTab(value || 'active')}
          >
            <Tabs.List>
              <Tabs.Tab value="active" p="md" fz={16}>
                Active Diagnoses
              </Tabs.Tab>
              <Tabs.Tab value="imported" p="md" fz={16}>
                Imported Diagnoses
              </Tabs.Tab>
            </Tabs.List>
          </Tabs>

          {currentTab === 'active' && (
            <ResourceTable
              key={`active-${refreshKey}`}
              patient={patient}
              item={setQuery(item, 'category:not=Problem HL7.CCDAR2')}
              availFields={availFields}
              renderColumn={renderColumn}
              defaultSort="recorded-date"
              blockRemove
              preprocessList={preprocessList}
              headerCellClassName={headerCellClassName}
            />
          )}

          {currentTab === 'imported' && (
            <ResourceTable
              key={`imported-${refreshKey}`}
              patient={patient}
              item={setQuery(item, 'category=Problem HL7.CCDAR2')}
              availFields={availFields}
              renderColumn={renderColumn}
              defaultSort="recorded-date"
              blockRemove
              preprocessList={preprocessList}
              headerCellClassName={headerCellClassName}
            />
          )}
        </>
      )}
    </AddResourceSection>
  )
}
